import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2, Sparkles, Image as ImageIcon, Download, RefreshCw,
  Wand2, Palette, ChevronLeft, LogOut, Type, Layers,
  Save, History as HistoryIcon, Trash2, CopyPlus,
  Upload, X, Highlighter,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { authApi } from "../admin/api";
import { studioApi } from "../studio/api";
import {
  TEMPLATE_OPTIONS, FORMAT_OPTIONS, CTA_PRESETS, SIGNAL_COLOR_OPTIONS,
  CTA_COLOR_OPTIONS,
  renderTemplate, loadImage,
} from "../studio/canvas";
import { usePageMeta } from "../hooks/usePageMeta";
import { readPrefill } from "../planner/helpers";

const LOGO_SRC = "/brand-assets/daos-logo.png";
const UPLOAD_MAX_BYTES = 4 * 1024 * 1024; // 4MB cap for user uploads

const PREVIEW_MAX = 600; // px on the long side
const THUMB_MAX = 360;   // px on the long side for the saved gallery thumbnail

function aspectFromFormat(formatId) {
  return FORMAT_OPTIONS.find((f) => f.id === formatId)?.aspect || "square";
}

// Read a File object as a data: URL.
function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Build a JPEG thumbnail (base64, no data: prefix) from the main canvas.
function buildThumbnail(srcCanvas) {
  const scale = THUMB_MAX / Math.max(srcCanvas.width, srcCanvas.height);
  const tw = Math.round(srcCanvas.width * scale);
  const th = Math.round(srcCanvas.height * scale);
  const off = document.createElement("canvas");
  off.width = tw;
  off.height = th;
  const ictx = off.getContext("2d");
  ictx.imageSmoothingEnabled = true;
  ictx.imageSmoothingQuality = "high";
  // Solid background so JPEG has no transparency artifacts
  ictx.fillStyle = "#050505";
  ictx.fillRect(0, 0, tw, th);
  ictx.drawImage(srcCanvas, 0, 0, tw, th);
  const dataUrl = off.toDataURL("image/jpeg", 0.78);
  return dataUrl.replace(/^data:image\/jpeg;base64,/, "");
}

export default function Studio() {
  usePageMeta({ title: "Studio · Daos Ads" });

  // Defensive SEO: ensure search engines never index /studio even if a token leaks.
  useEffect(() => {
    let tag = document.querySelector('meta[name="robots"]');
    const prev = tag?.getAttribute("content") || null;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", "noindex,nofollow");
    return () => {
      if (prev === null) tag?.remove();
      else tag?.setAttribute("content", prev);
    };
  }, []);

  const navigate = useNavigate();

  const [templateId, setTemplateId] = useState("frase");
  const [formatId, setFormatId] = useState("ig-square");
  const [imageModel, setImageModel] = useState("gemini");

  const [topic, setTopic] = useState("");
  const [service, setService] = useState("");

  const [headline, setHeadline] = useState("Gana más, pierde cero.");
  const [subtitle, setSubtitle] = useState("Automatización con IA, Respond.io y ads conectados a tu CRM.");
  const [cta, setCta] = useState("Agenda una llamada");
  const [author, setAuthor] = useState("David Osorio");

  // Word-styling controls (per brand color system).
  // Inputs accept multiple phrases separated by commas. A phrase may contain
  // spaces (e.g. "Gana más, pierde, cero" → 3 phrases).
  const [gradientWordsRaw, setGradientWordsRaw] = useState("");
  const [signalWordsRaw, setSignalWordsRaw] = useState("");
  const [signalColor, setSignalColor] = useState("none"); // none | green | amber | red
  const [ctaColor, setCtaColor] = useState("gradient");

  const gradientPhrases = useMemo(
    () => gradientWordsRaw.split(",").map((p) => p.trim()).filter(Boolean),
    [gradientWordsRaw],
  );
  const signalPhrases = useMemo(
    () => signalWordsRaw.split(",").map((p) => p.trim()).filter(Boolean),
    [signalWordsRaw],
  );

  const [imagePrompt, setImagePrompt] = useState(
    "Skyline nocturno minimalista con destellos cálidos naranja, atmósfera cinematográfica premium."
  );
  const [bgImage, setBgImage] = useState(null);
  const [bgMeta, setBgMeta] = useState(null); // { base64, mime, model, prompt }
  const [logoImg, setLogoImg] = useState(null);

  const [loadingCopy, setLoadingCopy] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);

  // History
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const canvasRef = useRef(null);

  const format = useMemo(() => FORMAT_OPTIONS.find((f) => f.id === formatId), [formatId]);

  // Load brand logo once
  useEffect(() => {
    loadImage(LOGO_SRC).then(setLogoImg).catch(() => setLogoImg(null));
  }, []);

  // Prefill coming from /planner ("Abrir en Studio")
  useEffect(() => {
    const pre = readPrefill();
    if (!pre || pre.target !== "art") return;
    if (pre.templateId) setTemplateId(pre.templateId);
    if (pre.formatId) setFormatId(pre.formatId);
    setHeadline(pre.headline || "");
    setSubtitle(pre.subtitle || "");
    setCta(pre.cta || "");
    setGradientWordsRaw(pre.gradientWords || "");
    setSignalWordsRaw(pre.signalWords || "");
    setSignalColor(pre.signalColor || "none");
    setCtaColor(pre.ctaColor || "gradient");
    if (pre.imagePrompt) setImagePrompt(pre.imagePrompt);
    if (pre.backgroundDataUrl) {
      loadImage(pre.backgroundDataUrl)
        .then((img) => {
          setBgImage(img);
          setBgMeta({
            base64: String(pre.backgroundDataUrl).split(",")[1] || "",
            mime: "image/jpeg", model: "planner", prompt: pre.imagePrompt || "planner",
          });
        })
        .catch(() => {});
    }
    toast.success("Pieza del planner cargada");
  }, []);

  // Re-render canvas whenever any input changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !format) return;
    canvas.width = format.width;
    canvas.height = format.height;
    const ctx = canvas.getContext("2d");
    renderTemplate(ctx, {
      width: format.width,
      height: format.height,
      templateId,
      content: { headline, subtitle, cta, author },
      background: bgImage,
      logo: logoImg,
      gradientWords: gradientPhrases,
      signalWords: signalPhrases,
      signalColor: signalColor === "none" ? null : signalColor,
      ctaColor,
    });
  }, [templateId, format, headline, subtitle, cta, author, bgImage, logoImg,
      gradientPhrases, signalPhrases, signalColor, ctaColor]);

  const previewStyle = useMemo(() => {
    if (!format) return {};
    const scale = PREVIEW_MAX / Math.max(format.width, format.height);
    return {
      width: format.width * scale,
      height: format.height * scale,
      maxWidth: "100%",
    };
  }, [format]);

  const onGenerateCopy = async () => {
    if (!topic.trim()) {
      toast.error("Escribe el tema o contexto del post");
      return;
    }
    setLoadingCopy(true);
    try {
      const data = await studioApi.generateCopy({
        template: templateId,
        topic: topic.trim(),
        service: service.trim() || null,
      });
      setHeadline(data.headline);
      setSubtitle(data.subtitle);
      setCta(data.cta);
      toast.success("Copy generado con Claude Sonnet 4.5");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo generar el copy");
    } finally {
      setLoadingCopy(false);
    }
  };

  const onGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error("Describe la imagen que quieres generar");
      return;
    }
    setLoadingImage(true);
    try {
      const data = await studioApi.generateImage({
        prompt: imagePrompt.trim(),
        model: imageModel,
        aspect: aspectFromFormat(formatId),
      });
      const mime = data.mime_type || "image/png";
      const src = `data:${mime};base64,${data.image_base64}`;
      const img = await loadImage(src);
      setBgImage(img);
      setBgMeta({ base64: data.image_base64, mime, model: data.model, prompt: imagePrompt.trim() });
      toast.success(`Imagen generada · ${data.model}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo generar la imagen");
    } finally {
      setLoadingImage(false);
    }
  };

  const onClearImage = () => {
    setBgImage(null);
    setBgMeta(null);
  };

  // ===== Upload propia =====
  const handleUploadedFile = useCallback(async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Solo se aceptan imágenes (PNG, JPG, WebP)");
      return;
    }
    if (file.size > UPLOAD_MAX_BYTES) {
      toast.error("Imagen demasiado grande (máx 4MB)");
      return;
    }
    setUploadingImage(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      const img = await loadImage(dataUrl);
      const mime = file.type || "image/png";
      const b64 = String(dataUrl).split(",")[1] || "";
      setBgImage(img);
      setBgMeta({ base64: b64, mime, model: "upload", prompt: file.name });
      toast.success(`Imagen aplicada · ${file.name}`);
    } catch (err) {
      toast.error("No se pudo leer la imagen");
    } finally {
      setUploadingImage(false);
    }
  }, []);

  const onUploadInput = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    handleUploadedFile(file);
  };

  const onDropImage = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    handleUploadedFile(file);
  }, [handleUploadedFile]);

  // ===== History =====
  const refreshHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const data = await studioApi.listAssets({ limit: 24 });
      setHistory(data.items || []);
    } catch (err) {
      // Silent fail on initial load; explicit retry via UI button
      console.warn("history load failed", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { refreshHistory(); }, [refreshHistory]);

  const saveCurrentAsset = useCallback(async ({ silent = false } = {}) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    setSaving(true);
    try {
      const thumb = buildThumbnail(canvas);
      const payload = {
        template: templateId,
        format_id: formatId,
        headline, subtitle, cta, author,
        gradient_words: gradientPhrases,
        signal_words: signalPhrases,
        signal_color: signalColor === "none" ? null : signalColor,
        cta_color: ctaColor,
        image_prompt: bgMeta?.prompt || null,
        image_model: bgMeta?.model || null,
        background_base64: bgMeta?.base64 || null,
        background_mime: bgMeta?.mime || "image/png",
        thumbnail_base64: thumb,
        thumbnail_mime: "image/jpeg",
      };
      const saved = await studioApi.saveAsset(payload);
      setHistory((prev) => [saved, ...prev].slice(0, 60));
      if (!silent) toast.success("Asset guardado en el historial");
      return saved;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar el asset");
      return null;
    } finally {
      setSaving(false);
    }
  }, [templateId, formatId, headline, subtitle, cta, author, bgMeta,
      gradientPhrases, signalPhrases, signalColor, ctaColor]);

  const onLoadAsset = async (item) => {
    try {
      const full = await studioApi.getAsset(item.id);
      setTemplateId(full.template);
      setFormatId(full.format_id);
      setHeadline(full.headline || "");
      setSubtitle(full.subtitle || "");
      setCta(full.cta || "");
      setAuthor(full.author || "David Osorio");
      setGradientWordsRaw((full.gradient_words || []).join(", "));
      setSignalWordsRaw(
        ((full.signal_words && full.signal_words.length)
          ? full.signal_words
          : (full.signal_word ? [full.signal_word] : [])
        ).join(", ")
      );
      setSignalColor(full.signal_color || "none");
      setCtaColor(full.cta_color || "gradient");
      if (full.background_base64) {
        const mime = full.background_mime || "image/png";
        const img = await loadImage(`data:${mime};base64,${full.background_base64}`);
        setBgImage(img);
        setBgMeta({
          base64: full.background_base64,
          mime,
          model: full.image_model || null,
          prompt: full.image_prompt || "",
        });
        if (full.image_prompt) setImagePrompt(full.image_prompt);
      } else {
        setBgImage(null);
        setBgMeta(null);
      }
      toast.success("Asset cargado · listo para remix");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo cargar el asset");
    }
  };

  const onDeleteAsset = async (id) => {
    const prev = history;
    setHistory((cur) => cur.filter((x) => x.id !== id));
    try {
      await studioApi.deleteAsset(id);
      toast.success("Asset eliminado");
    } catch (err) {
      setHistory(prev);
      toast.error("No se pudo eliminar");
    }
  };

  // "Duplicar y editar" = load the asset into the editor + ask Claude for an
  // A/B variant of the copy (same template/format/background, different angle).
  // Result is left UNSAVED so the user can review & hit Guardar to persist the variant.
  const onDuplicateAsset = async (item) => {
    if (duplicatingId) return;
    // Snapshot current editor state so we can roll back if the variant call fails
    // (otherwise the user would be left with the source loaded and might Guardar
    // an exact duplicate by mistake).
    const snapshot = {
      templateId, formatId, headline, subtitle, cta, author,
      bgImage, bgMeta, imagePrompt,
      gradientWordsRaw, signalWordsRaw, signalColor, ctaColor,
    };
    setDuplicatingId(item.id);
    try {
      // 1) Restore everything from the source asset (template, format, background, texts)
      const full = await studioApi.getAsset(item.id);
      setTemplateId(full.template);
      setFormatId(full.format_id);
      setHeadline(full.headline || "");
      setSubtitle(full.subtitle || "");
      setCta(full.cta || "");
      setAuthor(full.author || "David Osorio");
      setGradientWordsRaw((full.gradient_words || []).join(", "));
      setSignalWordsRaw(
        ((full.signal_words && full.signal_words.length)
          ? full.signal_words
          : (full.signal_word ? [full.signal_word] : [])
        ).join(", ")
      );
      setSignalColor(full.signal_color || "none");
      setCtaColor(full.cta_color || "gradient");
      if (full.background_base64) {
        const mime = full.background_mime || "image/png";
        const img = await loadImage(`data:${mime};base64,${full.background_base64}`);
        setBgImage(img);
        setBgMeta({
          base64: full.background_base64,
          mime,
          model: full.image_model || null,
          prompt: full.image_prompt || "",
        });
        if (full.image_prompt) setImagePrompt(full.image_prompt);
      } else {
        setBgImage(null);
        setBgMeta(null);
      }

      // 2) Ask Claude for an A/B variant of the copy
      try {
        const variant = await studioApi.variantCopy({
          template: full.template,
          headline: full.headline,
          subtitle: full.subtitle,
          cta: full.cta,
          author: full.author,
          service: null,
        });
        setHeadline(variant.headline);
        setSubtitle(variant.subtitle);
        setCta(variant.cta);
        toast.success("Variante A/B lista · revisa y pulsa Guardar para persistirla");
      } catch (variantErr) {
        // Variant failed AFTER source was loaded → roll back to the user's prior state
        setTemplateId(snapshot.templateId);
        setFormatId(snapshot.formatId);
        setHeadline(snapshot.headline);
        setSubtitle(snapshot.subtitle);
        setCta(snapshot.cta);
        setAuthor(snapshot.author);
        setBgImage(snapshot.bgImage);
        setBgMeta(snapshot.bgMeta);
        setImagePrompt(snapshot.imagePrompt);
        setGradientWordsRaw(snapshot.gradientWordsRaw);
        setSignalWordsRaw(snapshot.signalWordsRaw);
        setSignalColor(snapshot.signalColor);
        setCtaColor(snapshot.ctaColor);
        const detail = variantErr?.response?.data?.detail;
        toast.error(typeof detail === "string" ? `Variante: ${detail}` : "No se pudo generar la variante. Lienzo restaurado.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo duplicar el asset");
    } finally {
      setDuplicatingId(null);
    }
  };

  const onDownload = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    link.download = `daos-${templateId}-${format.id}-${stamp}.png`;
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success("PNG descargado");
    // Auto-save to history (silent so the user only sees one toast).
    await saveCurrentAsset({ silent: true });
  };

  const onReset = () => {
    setHeadline("Gana más, pierde cero.");
    setSubtitle("Automatización con IA, Respond.io y ads conectados a tu CRM.");
    setCta("Agenda una llamada");
    setAuthor("David Osorio");
    setBgImage(null);
    setBgMeta(null);
    setTopic("");
    setService("");
    setGradientWordsRaw("");
    setSignalWordsRaw("");
    setSignalColor("none");
    setCtaColor("gradient");
    toast.message("Lienzo reseteado");
  };

  const onLogout = () => {
    authApi.logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden">
      {/* Ambient backdrop */}
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1200px] h-[900px] rounded-full opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, rgba(255,138,0,0.18), transparent 70%)" }}
      />
      <div className="absolute inset-0 grain pointer-events-none" />

      {/* Slim toolbar header */}
      <header className="relative border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="Daos Ads" className="h-9 w-auto" />
            <div className="leading-tight border-l border-white/10 pl-3">
              <div className="font-display font-bold text-white text-[14px] tracking-tight">Studio</div>
              <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase">Asset Generator</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/planner"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20"
              data-testid="studio-link-planner"
            >
              Planner
            </Link>
            <Link
              to="/admin"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20"
              data-testid="studio-back-admin"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> CRM
            </Link>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20"
              data-testid="studio-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="relative px-5 sm:px-8 py-6 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        {/* ===== LEFT: CONTROLS ===== */}
        <aside className="space-y-5" data-testid="studio-controls">
          {/* Step 1: Format + template */}
          <Card icon={Layers} title="Formato y plantilla">
            <Label>Formato</Label>
            <Select value={formatId} onChange={setFormatId} testid="format-select">
              {FORMAT_OPTIONS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </Select>

            <Label className="mt-4">Plantilla</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {TEMPLATE_OPTIONS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTemplateId(t.id)}
                  data-testid={`tpl-${t.id}`}
                  className={`text-left text-[12px] px-3 py-2.5 rounded-lg border transition font-display ${
                    templateId === t.id
                      ? "border-[#ff8a00] bg-[#ff8a00]/10 text-white"
                      : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Card>

          {/* Step 2: Copy with Claude */}
          <Card icon={Wand2} title="Copy con Claude Sonnet 4.5">
            <Label>Tema / contexto</Label>
            <Input
              value={topic}
              onChange={setTopic}
              placeholder="Ej: por qué las pymes pierden 80% de leads en WhatsApp"
              testid="topic-input"
            />
            <Label className="mt-3">Servicio relacionado (opcional)</Label>
            <Input
              value={service}
              onChange={setService}
              placeholder="Ej: Respond.io, Google Ads, Funnels..."
              testid="service-input"
            />
            <button
              type="button"
              onClick={onGenerateCopy}
              disabled={loadingCopy}
              className="btn-primary w-full mt-4 justify-center disabled:opacity-60"
              data-testid="generate-copy-btn"
            >
              {loadingCopy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loadingCopy ? "Generando..." : "Generar copy con IA"}
            </button>
          </Card>

          {/* Step 3: Image — IA or upload propio */}
          <Card icon={ImageIcon} title="Imagen de fondo">
            <Label>Origen</Label>
            <div className="grid grid-cols-2 gap-2 mt-1 mb-3">
              <label
                onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDropImage}
                className={`relative cursor-pointer text-[12px] px-3 py-3 rounded-lg border border-dashed transition font-display flex items-center justify-center gap-1.5 ${
                  dragActive
                    ? "border-[#ff8a00] bg-[#ff8a00]/15 text-white"
                    : "border-white/15 bg-white/[0.02] text-zinc-300 hover:border-[#ff8a00]/60 hover:text-white"
                }`}
                data-testid="upload-image-zone"
              >
                {uploadingImage
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Upload className="w-3.5 h-3.5" />}
                {uploadingImage ? "Subiendo..." : "Subir mi imagen"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={onUploadInput}
                  data-testid="upload-image-input"
                />
              </label>
              <div className="text-[12px] px-3 py-3 rounded-lg border border-white/10 bg-white/[0.02] text-zinc-400 font-display flex items-center justify-center gap-1.5">
                <Wand2 className="w-3.5 h-3.5 text-[#ff8a00]" />
                o generá con IA ↓
              </div>
            </div>

            {bgImage && (
              <div className="flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-lg bg-[#0A0A0A] border border-white/10">
                <span className="text-[11px] text-zinc-400 truncate flex-1">
                  Fondo activo · <span className="text-zinc-200">{bgMeta?.model || "—"}</span>
                </span>
                <button
                  type="button"
                  onClick={onClearImage}
                  className="text-zinc-400 hover:text-white p-1 rounded transition"
                  data-testid="clear-image-btn"
                  title="Quitar fondo"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <Label>Modelo IA</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[
                { id: "gemini", label: "Nano Banana" },
                { id: "openai", label: "GPT Image 1" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setImageModel(m.id)}
                  data-testid={`img-model-${m.id}`}
                  className={`text-[12px] px-3 py-2.5 rounded-lg border transition font-display ${
                    imageModel === m.id
                      ? "border-[#ff8a00] bg-[#ff8a00]/10 text-white"
                      : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <Label className="mt-3">Prompt visual</Label>
            <Textarea
              value={imagePrompt}
              onChange={setImagePrompt}
              rows={3}
              placeholder="Describe la escena: estilo, atmósfera, paleta..."
              testid="image-prompt"
            />
            <p className="text-[11px] text-zinc-500 mt-2">
              Overlay <code className="text-zinc-300">rgba(5,5,5,0.72)</code> + glow naranja aplicados
              automáticamente sobre cualquier imagen (IA o subida) para mantener identidad Daos.
            </p>

            <button
              type="button"
              onClick={onGenerateImage}
              disabled={loadingImage}
              className="btn-primary w-full mt-3 justify-center disabled:opacity-60"
              data-testid="generate-image-btn"
            >
              {loadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Palette className="w-4 h-4" />}
              {loadingImage ? "Generando..." : "Generar imagen con IA"}
            </button>
          </Card>

          {/* Step 4: Manual text edits */}
          <Card icon={Type} title="Texto en el lienzo">
            <Label>Titular</Label>
            <Textarea value={headline} onChange={setHeadline} rows={2} testid="headline-input" />
            <Label className="mt-3">Subtítulo</Label>
            <Textarea value={subtitle} onChange={setSubtitle} rows={2} testid="subtitle-input" />

            <Label className="mt-3">CTA · presets</Label>
            <Select
              value={CTA_PRESETS.find((p) => p.value === cta)?.id || "custom"}
              onChange={(id) => {
                if (id === "custom") return; // keep current
                const preset = CTA_PRESETS.find((p) => p.id === id);
                if (preset) setCta(preset.value);
              }}
              testid="cta-preset-select"
            >
              {CTA_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
              <option value="custom">Personalizado…</option>
            </Select>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label>CTA texto</Label>
                <Input value={cta} onChange={setCta} testid="cta-input" />
              </div>
              {templateId === "cita-personal" ? (
                <div>
                  <Label>Autor</Label>
                  <Input value={author} onChange={setAuthor} testid="author-input" />
                </div>
              ) : (
                <div className="opacity-40 pointer-events-none select-none" title="El autor solo se muestra en la plantilla 'Cita personal'">
                  <Label>Autor</Label>
                  <Input value={author} onChange={() => {}} testid="author-input" />
                </div>
              )}
            </div>
          </Card>

          {/* Step 5: Word styling */}
          <Card icon={Highlighter} title="Palabras destacadas">
            <Label>Palabras en degradado</Label>
            <Input
              value={gradientWordsRaw}
              onChange={setGradientWordsRaw}
              placeholder="ej: Gana más, pierde, cero"
              testid="gradient-words-input"
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              Separa frases por <span className="text-zinc-300">coma</span>. Cada frase puede contener espacios — &ldquo;Gana más&rdquo; cuenta como una sola frase.
            </p>

            <Label className="mt-3">Palabras de señal</Label>
            <Input
              value={signalWordsRaw}
              onChange={setSignalWordsRaw}
              placeholder="ej: triplica, 30 días"
              testid="signal-word-input"
            />
            <div className="mt-2">
              <Label>Color de señal</Label>
              <div className="flex gap-1.5">
                {SIGNAL_COLOR_OPTIONS.map((opt) => {
                  const active = signalColor === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setSignalColor(opt.id)}
                      title={opt.label}
                      data-testid={`signal-color-${opt.id}`}
                      className={`w-9 h-9 rounded-lg border grid place-items-center transition ${
                        active
                          ? "border-white ring-2 ring-[#ff8a00]/40"
                          : "border-white/15 hover:border-white/30"
                      }`}
                      style={opt.swatch ? { background: opt.swatch } : { background: "rgba(255,255,255,0.04)" }}
                    >
                      {!opt.swatch && <X className="w-3.5 h-3.5 text-zinc-500" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">
              Múltiples frases separadas por coma. La señal tiene prioridad sobre el degradado en conflictos.
            </p>
          </Card>

          {/* Step 6: CTA color */}
          <Card icon={Palette} title="Color del CTA">
            <div className="grid grid-cols-3 gap-2">
              {CTA_COLOR_OPTIONS.map((opt) => {
                const active = ctaColor === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCtaColor(opt.id)}
                    title={opt.label}
                    data-testid={`cta-color-${opt.id}`}
                    className={`group relative rounded-lg overflow-hidden border transition ${
                      active
                        ? "border-white ring-2 ring-[#ff8a00]/40"
                        : "border-white/15 hover:border-white/30"
                    }`}
                  >
                    <div
                      className="h-7 w-full"
                      style={{ background: opt.swatch }}
                    />
                    <div className="text-[10px] font-display py-1.5 text-zinc-300 group-hover:text-white text-center">
                      {opt.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-3 sticky bottom-4">
            <button
              type="button"
              onClick={onReset}
              className="px-4 py-3 rounded-xl border border-white/10 text-zinc-300 hover:border-white/25 text-sm font-display font-semibold"
              data-testid="reset-btn"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => saveCurrentAsset({ silent: false })}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-[#ff8a00]/40 bg-[#ff8a00]/10 text-white hover:bg-[#ff8a00]/20 transition text-sm font-display font-semibold disabled:opacity-60"
              data-testid="save-btn"
              title="Guardar sin descargar"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
            <button
              type="button"
              onClick={onDownload}
              className="btn-primary flex-1 justify-center"
              data-testid="download-btn"
            >
              <Download className="w-4 h-4" /> Descargar PNG
            </button>
          </div>
        </aside>

        {/* ===== RIGHT: PREVIEW ===== */}
        <section className="relative" data-testid="studio-preview-section">
          <div className="grad-border p-5 sm:p-8 relative">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="eyebrow">Preview en vivo</div>
                <div className="font-display font-bold text-white text-lg mt-1">
                  {format?.label}
                </div>
              </div>
              <div className="text-[11px] text-zinc-500">
                Resolución real {format?.width}×{format?.height}
              </div>
            </div>

            <div className="flex items-center justify-center min-h-[60vh]">
              <div
                style={previewStyle}
                className="rounded-lg overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_-20px_rgba(255,138,0,0.35)]"
                data-testid="studio-preview-frame"
              >
                <canvas
                  ref={canvasRef}
                  style={{ width: "100%", height: "100%", display: "block" }}
                  data-testid="studio-canvas"
                />
              </div>
            </div>
          </div>

          {/* ===== HISTORY ===== */}
          <div className="grad-border p-5 sm:p-6 mt-6" data-testid="studio-history">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-[#ff8a00]/15 grid place-items-center">
                  <HistoryIcon className="w-3.5 h-3.5 text-[#ff8a00]" />
                </span>
                <div>
                  <div className="eyebrow">Historial reutilizable</div>
                  <div className="font-display font-bold text-white text-[15px] mt-0.5">
                    Tus assets guardados {history.length > 0 && (
                      <span className="text-zinc-500 font-normal text-[12px] ml-1">· {history.length}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={refreshHistory}
                disabled={loadingHistory}
                className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400 hover:text-white transition px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20 disabled:opacity-50"
                data-testid="history-refresh"
              >
                {loadingHistory ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Refrescar
              </button>
            </div>

            {history.length === 0 && !loadingHistory && (
              <div className="text-center py-10 px-4 rounded-xl border border-dashed border-white/10 text-zinc-500 text-sm" data-testid="history-empty">
                Aún no tienes assets guardados. Descarga o pulsa <span className="text-zinc-300">Guardar</span> y aparecerán aquí para remixar.
              </div>
            )}

            {history.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="history-grid">
                {history.map((item) => (
                  <HistoryCard
                    key={item.id}
                    item={item}
                    onLoad={() => onLoadAsset(item)}
                    onDelete={() => onDeleteAsset(item.id)}
                    onDuplicate={() => onDuplicateAsset(item)}
                    duplicating={duplicatingId === item.id}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: { background: "#0a0a0a", border: "1px solid rgba(255,138,0,0.25)", color: "#fff" },
        }}
      />
    </div>
  );
}

// ============================================================
// UI primitives (kept local — match existing AdminLogin styling)
// ============================================================

function Card({ icon: Icon, title, children }) {
  return (
    <div className="grad-border p-5 relative overflow-hidden">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg bg-[#ff8a00]/15 grid place-items-center">
          <Icon className="w-3.5 h-3.5 text-[#ff8a00]" />
        </span>
        <div className="font-display font-bold text-white text-[13px] tracking-tight">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Label({ children, className = "" }) {
  return (
    <label className={`block text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-display font-semibold mb-1.5 ${className}`}>
      {children}
    </label>
  );
}

function Input({ value, onChange, testid, ...rest }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
      {...rest}
    />
  );
}

function Textarea({ value, onChange, testid, rows = 3, ...rest }) {
  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none resize-none"
      {...rest}
    />
  );
}

function Select({ value, onChange, children, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none"
    >
      {children}
    </select>
  );
}

function HistoryCard({ item, onLoad, onDelete, onDuplicate, duplicating }) {
  const fmt = FORMAT_OPTIONS.find((f) => f.id === item.format_id);
  const tpl = TEMPLATE_OPTIONS.find((t) => t.id === item.template);
  const src = `data:${item.thumbnail_mime || "image/jpeg"};base64,${item.thumbnail_base64}`;
  const date = item.created_at ? new Date(item.created_at) : null;
  const dateLabel = date
    ? date.toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) +
      " · " +
      date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    : "";
  return (
    <div className="group relative" data-testid={`history-item-${item.id}`}>
      <button
        type="button"
        onClick={onLoad}
        title={item.headline}
        className="block w-full text-left rounded-lg overflow-hidden ring-1 ring-white/10 hover:ring-[#ff8a00]/60 transition focus:outline-none focus:ring-2 focus:ring-[#ff8a00]"
        data-testid={`history-load-${item.id}`}
      >
        <div
          className="w-full bg-[#050505]"
          style={{ aspectRatio: fmt ? `${fmt.width} / ${fmt.height}` : "1 / 1" }}
        >
          <img
            src={src}
            alt={item.headline}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-2.5 bg-black/40">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#ff8a00] font-display font-semibold truncate">
            {tpl?.label || item.template}
          </div>
          <div className="text-[11px] text-white font-display font-semibold truncate mt-0.5">
            {item.headline}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
            {fmt?.label?.split(" · ")[1] || item.format_id}{dateLabel ? " · " + dateLabel : ""}
          </div>
        </div>
      </button>

      {/* Hover actions */}
      <div className="absolute top-1.5 right-1.5 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          disabled={duplicating}
          className="w-7 h-7 rounded-full bg-black/70 border border-[#ff8a00]/40 text-[#ff8a00] hover:bg-[#ff8a00]/20 hover:border-[#ff8a00] grid place-items-center transition disabled:opacity-60"
          title="Duplicar y editar (variante A/B con IA)"
          data-testid={`history-duplicate-${item.id}`}
        >
          {duplicating
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <CopyPlus className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="w-7 h-7 rounded-full bg-black/70 border border-white/10 text-zinc-300 hover:text-white hover:bg-red-500/80 hover:border-red-400 grid place-items-center transition"
          title="Eliminar"
          data-testid={`history-delete-${item.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
